import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from '@tanstack/react-router'
import { Search, Loader2 } from 'lucide-react'
import { EmployeeAvatar } from '@/components/vacation/EmployeeAvatar'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/utils'
import { useEmployeeAutocomplete } from '@/hooks/useWorkforce'
import { debounce } from '@/utils/debounce'

interface EmployeeSuggestion {
  employeeId: string
  name: string
  email?: string
  pictureUrl?: string
  department?: string
}

interface EmployeeAutocompleteProps {
  onSelect?: (employeeId: string, employee?: EmployeeSuggestion) => void
  placeholder?: string
  className?: string
  navigateOnSelect?: boolean
  disabled?: boolean
}

export const EmployeeAutocomplete: React.FC<EmployeeAutocompleteProps> = ({
  onSelect,
  placeholder = 'Buscar funcionário por nome ou email...',
  className,
  navigateOnSelect = true,
  disabled = false
}) => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Debounce search input
  useEffect(() => {
    const debouncedUpdate = debounce((value: string) => {
      setDebouncedSearch(value)
    }, 300)

    debouncedUpdate(searchTerm)

    return () => {
      debouncedUpdate.cancel()
    }
  }, [searchTerm])

  // Fetch suggestions
  const { suggestions, loading } = useEmployeeAutocomplete(debouncedSearch, {
    enabled: debouncedSearch.length >= 2,
    limit: 10
  })

  // Show/hide dropdown based on search and suggestions
  useEffect(() => {
    const shouldBeOpen = searchTerm.length >= 2 && (loading || suggestions.length > 0)
    setIsOpen(shouldBeOpen)
    setSelectedIndex(-1)

    // Calculate dropdown position
    if (shouldBeOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
  }, [searchTerm, loading, suggestions.length])

  // Update position on scroll/resize
  useEffect(() => {
    if (!isOpen) return

    const updatePosition = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect()
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width
        })
      }
    }

    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          const suggestion = suggestions[selectedIndex]
          handleSelect(suggestion.employeeId, suggestion)
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        inputRef.current?.blur()
        break
    }
  }

  // Handle suggestion selection
  const handleSelect = (employeeId: string, suggestion?: EmployeeSuggestion) => {
    setIsOpen(false)
    setSearchTerm('')
    setDebouncedSearch('')

    if (onSelect) {
      onSelect(employeeId, suggestion)
    }

    if (navigateOnSelect) {
      navigate({ to: `/workforce/employees/${employeeId}` })
    }
  }

  return (
    <div className={cn('relative w-full', className)}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray pointer-events-none z-10" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (searchTerm.length >= 2 && !disabled) setIsOpen(true)
          }}
          disabled={disabled}
          className="!pl-12 pr-9"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
            <Loader2 className="h-4 w-4 animate-spin text-brand-gray" />
          </div>
        )}
      </div>

      {/* Dropdown Suggestions - Rendered via Portal */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="bg-brand-dark-lighter border border-brand-gray/30 rounded-monumenta shadow-lg max-h-[300px] overflow-y-auto"
          style={{
            position: 'absolute',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 99999
          }}
        >
          {loading && suggestions.length === 0 && (
            <div className="p-4 text-center text-sm text-brand-gray">
              Buscando...
            </div>
          )}

          {!loading && suggestions.length === 0 && searchTerm.length >= 2 && (
            <div className="p-4 text-center text-sm text-brand-gray">
              Nenhum funcionário encontrado
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="py-1">
              {suggestions.map((suggestion, index) => (
                <button
                  key={suggestion.employeeId}
                  type="button"
                  onClick={() => handleSelect(suggestion.employeeId, suggestion)}
                  className={cn(
                    'w-full px-3 py-2 flex items-start gap-3 hover:bg-brand-gray/10 transition-colors cursor-pointer text-left text-white',
                    selectedIndex === index && 'bg-brand-gray/20'
                  )}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  {/* Avatar */}
                  <EmployeeAvatar
                    src={suggestion.pictureUrl}
                    name={suggestion.name}
                    size="sm"
                  />

                  {/* Info - stacked layout */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-white">{suggestion.name}</div>
                    <div className="text-xs text-brand-gray truncate">
                      {suggestion.email}
                    </div>
                    {/* Department Badge - below email */}
                    {suggestion.department && (
                      <Badge variant="outline" className="mt-1 text-xs max-w-full truncate">
                        {suggestion.department}
                      </Badge>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Helper Text */}
      {searchTerm.length > 0 && searchTerm.length < 2 && (
        <p className="mt-1 text-xs text-brand-gray">
          Digite pelo menos 2 caracteres para buscar
        </p>
      )}
    </div>
  )
}
